import { BaseViewStore } from 'core/stores';
import { LookupStore } from 'modules/lookup/stores';
import { OrderStore } from 'modules/order/stores';
import { ReplacementForm } from 'modules/order/forms';
import { observable, action, makeObservable } from 'mobx';
import { lookupRouteName, sourceOfChange, viewStoreName } from 'common/constants';
import { notificationError, translate } from 'core/utils';
import _ from 'lodash';
import { BaasicDropdownViewStore } from 'common/stores';
import { EditOrder, Replacement } from '../pages';

class ReplacementViewStore extends BaseViewStore {
	@observable selectedOrder = null;
	@observable requiredItemsMessage = '';
	@observable productsForSelection = [];
	isOrderUpdateRequired = false;

	form = new ReplacementForm({
		onSuccess: async () => {
			this.replaceOrder();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.store = new OrderStore(rootStore);
		this.replacementReasonDropdownStore = new BaasicDropdownViewStore({
			lookupRoute: lookupRouteName.replacementReason,
			field: this.form.$('replacementReasonId'),
			store: new LookupStore(rootStore),
		});

		this.initialize(this.modalParams.data.selectedItemId);
	}

	@action.bound
	initialize = async selectedItemId => {
		this.suspend();
		await this.getOrder(selectedItemId);
		if (!this.selectedOrder.shippingAddress1 && !this.modalParams.data.parentView.orderDetails) {
			this.rootStore.notificationStore.warning(['REPLACEMENTVIEW.MISSINGINFORMATION']);
			this.openModal(this.getEditOrderModalProps(this.selectedOrder));
		}
		this.calculateProductQuantityEligibileForSelection(this.selectedOrder);
		this.resume();
	};

	@action.bound
	getOrder = async selectedItemId => {
		this.selectedOrder = await this.store.getOrder(selectedItemId, {
			embed: [
				'orderLines',
				'orderLines.product',
				'orderLines.product.multiPackageProducts',
				'orderLines.product.multiPackageProducts.product',
				'shippingCoreCountry',
				'shippingCoreCountryState',
			],
		});
	};

	@action.bound
	replaceOrder = async () => {
		let notificationId = null;
		try {
			if (this.validateForm() && this.validateSelectedSkus()) {
				notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.REPLACE'));
				this.modalParams.close();
				if (this.modalParams.data.parentView.orderDetails) {
					await this.store.updateOrderInfo(
						this.modalParams.data.parentView.orderDetails,
						sourceOfChange.replacementModal
					);
				}
				await this.store.replace(
					{
						orderId: this.selectedOrder.id,
						itemsForReplace: this.getItemsForReplace(),
						...this.form.values(),
					},
					sourceOfChange.replacementModal
				);
				this.rootStore.notificationStore.success([
					'REPLACEMENTVIEW.SUCCESS',
					{ orderNumber: this.selectedOrder.orderNumber },
				]);
				this.modalParams.success();
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		if (notificationId != null) {
			this.rootStore.notificationStore.dismiss(notificationId);
		}
	};

	validateSelectedSkus = () => {
		if (
			_.some(this.productsForSelection, productForSelection => {
				return productForSelection.checkedQuantity > 0;
			})
		) {
			this.requiredItemsMessage = '';
			return true;
		} else {
			this.requiredItemsMessage = 'REPLACEMENTVIEW.REQUIREDSKU';
			return false;
		}
	};

	validateForm = async () => {
		if (!this.form.isValid) {
			this.form.showErrors();
		}
		return this.form.isValid;
	};

	@action.bound
	handleItemQuantityChange = async (selectedItem, isIncrease, event) => {
		_.forEach(this.productsForSelection, productForSelection => {
			if (selectedItem.sku === productForSelection.sku && selectedItem.id === productForSelection.id) {
				// On + button change.
				if (isIncrease && !event) {
					productForSelection.checkedQuantity = ++productForSelection.checkedQuantity;
					if (productForSelection.checkedQuantity > productForSelection.quantity) {
						productForSelection.checkedQuantity = productForSelection.quantity;
					}
				}
				// On - button change.
				else if (!isIncrease && !event) {
					productForSelection.checkedQuantity = --productForSelection.checkedQuantity;
					if (productForSelection.checkedQuantity < 0) {
						productForSelection.checkedQuantity = 0;
					}
				}
				// On checkbox change.
				else if (event) {
					productForSelection.checkedQuantity = isIncrease ? productForSelection.quantity : 0;
				}
				this.setCheckboxValue(productForSelection);
			}
		});
	};

	@action.bound
	setCheckboxValue = productForSelection => {
		productForSelection.isChecked = productForSelection.checkedQuantity === productForSelection.quantity;
		productForSelection.isPartiallyChecked =
			productForSelection.checkedQuantity !== productForSelection.quantity && productForSelection.checkedQuantity > 0;
	};

	@action.bound
	handleItemQuantityOnInputChange = async (item, value) => {
		let matchingItem = this.productsForSelection.find(i => i.sku === item.sku && i.id === item.id);
		if (!value) {
			matchingItem.checkedQuantity = 0;
		}
		if (value >= 0 && value <= matchingItem.quantity) {
			matchingItem.checkedQuantity = value;
		}
		this.setCheckboxValue(matchingItem);
	};

	@action.bound
	handleFocusChange = async (item, isBlur) => {
		let matchingItem = this.productsForSelection.find(i => i.sku === item.sku && i.id == item.id);
		if (isBlur) {
			if (matchingItem.checkedQuantity == '') {
				matchingItem.checkedQuantity = 0;
			}
		} else {
			if (matchingItem.checkedQuantity == 0) {
				matchingItem.checkedQuantity = '';
			}
		}
	};

	@action.bound
	calculateProductQuantityEligibileForSelection = order => {
		// If there is only one order line and product inside it is not multiProduct, it should be preselected.
		if (
			order.orderLines.length === 1 &&
			!order.orderLines[0].product.isMultiProduct &&
			order.orderLines[0].product.isShippable
		) {
			order.orderLines[0].sku = order.orderLines[0].product.mainSku;
			this.setProductsForSelection(order.orderLines[0], true);
		} else {
			_.forEach(order.orderLines, orderLine => {
				// Do not include unlinked order lines or order lines with inactive products.
				if (
					orderLine.productVirtualSkuId &&
					orderLine.product &&
					orderLine.product.isActive &&
					orderLine.product.isShippable
				) {
					if (!orderLine.product.isMultiProduct) {
						// Presented sku should be product main SKU.
						orderLine.sku = orderLine.product.mainSku;
						this.setProductsForSelection(orderLine);
					} else {
						// If orderLine contains multiPart product, product parts should be displayed to user.
						this.calculateProductQuantityMultiPackage(orderLine);
					}
				}
			});
		}
	};

	calculateProductQuantityMultiPackage = orderLine => {
		_.forEach(orderLine.product.multiPackageProducts, multiPackageProduct => {
			let multiPackagedLine = {
				id: orderLine.id,
				productId: multiPackageProduct.product.id,
				sku: multiPackageProduct.product.mainSku,
				quantity: multiPackageProduct.quantity * orderLine.quantity,
				productVirtualSkuId: orderLine.productVirtualSkuId,
				product: multiPackageProduct.product,
			};
			this.setProductsForSelection(multiPackagedLine);
		});
	};

	@action.bound
	setProductsForSelection(orderLine, selectItem = false) {
		orderLine.isChecked = selectItem;
		orderLine.isPartiallyChecked = false;
		orderLine.checkedQuantity = selectItem ? orderLine.quantity : 0;
		this.productsForSelection.push(orderLine);
	}

	@action.bound
	getItemsForReplace = () => {
		const itemsForReplace = [];
		_.forEach(this.productsForSelection, productForSelection => {
			if (productForSelection.checkedQuantity > 0) {
				itemsForReplace.push({
					productId: productForSelection.productId,
					productVirtualSkuId: productForSelection.productVirtualSkuId,
					orderLineId: productForSelection.id,
					sku: productForSelection.sku,
					quantity: productForSelection.checkedQuantity,
					onePerBox: productForSelection.product.onePerBox,
				});
			}
		});
		return itemsForReplace;
	};

	getEditOrderModalProps = selectedItem => {
		return {
			data: { parentView: this, selectedItemId: selectedItem.id, parentViewCallback: this.modalParams.onSuccess },
			component: EditOrder,
		};
	};
}

export default ReplacementViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/ReplacementViewStore.js